import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { CategoryCollapsibleMenuItem } from '@/components/layout/sidebar/CategoryCollapsibleMenuItem'
import { CategoryMenuItem } from '@/components/layout/sidebar/CategoryMenuItem'
import { CASINO_TYPE_ORIGINALS } from '@/config/casino'
import { useGetOriginalGameTypesQuery } from '@/store/api/originalApi'
import type { CasinoTypeId } from '@/types/casino'
import type { SidebarSection } from '@/types/config'
import _ from 'lodash'

interface CasinoMenuType {
	id: CasinoTypeId
	label: string
	icon: React.ComponentType<{ className?: string }> | string | null
	count?: number
}

interface SportMenuItem {
	id: string
	label: string
	icon: string | null
}

interface SidebarCategoryMenuProps {
	menuSection: SidebarSection
	casinoMenuTypes: CasinoMenuType[]
	sportMenuItems: SportMenuItem[]
	selectedCasinoTypeId: CasinoTypeId | null
	selectedSportsbookId: string | null
	onCasinoTypeClick: (typeId: CasinoTypeId) => void
	onSportMenuClick: (sportsbookId: string) => void
	onOriginalGameTypeClick: (gameId: string) => void
}

export function SidebarCategoryMenu({
	menuSection,
	casinoMenuTypes,
	sportMenuItems,
	selectedCasinoTypeId,
	selectedSportsbookId,
	onCasinoTypeClick,
	onSportMenuClick,
	onOriginalGameTypeClick,
}: SidebarCategoryMenuProps) {
	const { pathname } = useLocation()
	const [isOriginalsOpen, setIsOriginalsOpen] = useState(false)
	const { data: originalTypesResponse } = useGetOriginalGameTypesQuery(undefined, { skip: menuSection !== 'casino' })
	const originalGameTypes = originalTypesResponse?.data ?? []
	const isOnOriginalPlay = pathname.startsWith('/original/play/')
	const isOriginalsActive = selectedCasinoTypeId === CASINO_TYPE_ORIGINALS

	useEffect(() => {
		if (!isOriginalsActive && !isOnOriginalPlay) {
			setIsOriginalsOpen(false)
		}
	}, [isOriginalsActive, isOnOriginalPlay])

	if (menuSection === 'sport') {
		return (
			<div className='flex flex-col gap-1'>
				{sportMenuItems.map((item) => (
					<CategoryMenuItem key={item.id} icon={item.icon} label={item.label} isActive={selectedSportsbookId === item.id} onClick={() => onSportMenuClick(item.id)} />
				))}
			</div>
		)
	}

	return (
		<div className='flex flex-col gap-1'>
			{casinoMenuTypes.map((type) => {
				if (type.id === CASINO_TYPE_ORIGINALS) {
					return (
						<CategoryCollapsibleMenuItem
							key={type.id}
							icon={type.icon}
							label={type.label}
							count={originalTypesResponse?.total ?? originalGameTypes.length}
							isActive={isOriginalsActive}
							open={isOriginalsOpen}
							onOpenChange={(open) => {
								setIsOriginalsOpen(open)
								if (open) {
									onCasinoTypeClick(type.id)
								}
							}}
						>
							{originalGameTypes.map((gameType) => (
								<CategoryMenuItem
									key={gameType.id}
									icon={gameType.icon}
									label={_.capitalize(gameType.type)}
									isActive={pathname === `/original/play/${gameType.id}`}
									onClick={() => onOriginalGameTypeClick(gameType.id)}
								/>
							))}
						</CategoryCollapsibleMenuItem>
					)
				}

				return (
					<CategoryMenuItem
						key={type.id}
						icon={type.icon}
						label={type.label}
						count={type.count}
						isActive={selectedCasinoTypeId === type.id}
						onClick={() => onCasinoTypeClick(type.id)}
					/>
				)
			})}
		</div>
	)
}
